<script setup>
// SiteUp V2 — barra de ações do painel de contato (mockup aprovado 2026-08-11).
//
// Origem: as ações viviam como ícones de 16px DENTRO da linha do telefone, e a
// linha inteira só era renderizada quando `contact.phone_number` existia — sem
// número, elas sumiam da tela sem dizer por quê ("sumiram os botões").
//
// Aqui são a primeira coisa abaixo do nome: Ligar · Infos · Agendar, cada uma
// atrás do próprio pré-requisito. Ligar sem telefone fica APAGADO com o motivo
// escrito, nunca ausente. O WhatsApp saiu em 2026-08-11 a pedido do Lucas: a
// conversa já é o WhatsApp, abrir wa.me daqui era redundante.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  // Se a conta contratou voz E a caixa atual sabe ligar. Quem sabe disso é o
  // painel dono (feature flag + inbox WAHA); a decisão chega pronta. Recurso
  // não contratado não vira botão morto permanente — some mesmo.
  canCall: { type: Boolean, default: false },
  canSchedule: { type: Boolean, default: false },
  // custom_attributes da conversa. Sem anúncio na origem, sem botão fantasma.
  adAttributes: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['call', 'schedule', 'showAdInfo']);

const { t } = useI18n();

// O botão de WhatsApp saiu em 2026-08-11 (pedido do Lucas): a conversa JÁ É
// o WhatsApp — abrir wa.me daqui era redundante.
const hasPhone = computed(
  () =>
    String(props.contact?.phone_number || '').replace(/[^0-9]/g, '').length > 0
);

const hasAdOrigin = computed(() =>
  Boolean(props.adAttributes?.ctwa_clid || props.adAttributes?.ctwa_source_id)
);

const needsPhone = computed(() => t('CONTACT_PANEL.ACTIONS.NEEDS_PHONE'));

// O popover de agendar é desenhado pelo painel; sem a âncora do gatilho ele
// voltaria a ser posicionado por número mágico.
const onSchedule = event =>
  emit('schedule', event.currentTarget?.getBoundingClientRect?.() || null);
</script>

<template>
  <div
    v-if="canCall || hasAdOrigin || canSchedule"
    class="flex w-full flex-col gap-1.5"
  >
    <div class="flex w-full items-stretch gap-2">
      <button
        v-if="canCall"
        v-tooltip.top="hasPhone ? $t('VOIP_CALL.BUTTON.TOOLTIP') : needsPhone"
        type="button"
        :aria-disabled="!hasPhone"
        data-testid="contact-action-call"
        class="action-tile"
        :class="hasPhone ? 'action-tile--on' : 'action-tile--off'"
        @click="hasPhone && emit('call')"
      >
        <span
          class="i-ri-phone-line size-5"
          :class="{ 'text-n-blue-11': hasPhone }"
        />
        {{ $t('CONTACT_PANEL.ACTIONS.CALL') }}
      </button>

      <button
        v-if="hasAdOrigin"
        v-tooltip.top="$t('CONTACT_PANEL.ACTIONS.AD_INFO_TOOLTIP')"
        type="button"
        data-testid="contact-action-ad-info"
        class="action-tile action-tile--on"
        @click="emit('showAdInfo')"
      >
        <span class="i-ri-megaphone-line size-5 text-n-blue-11" />
        {{ $t('CONTACT_PANEL.ACTIONS.AD_INFO') }}
      </button>

      <button
        v-if="canSchedule"
        v-tooltip.top="$t('CONTACT_PANEL.SCHEDULE_ACTION')"
        type="button"
        data-testid="contact-action-schedule"
        class="action-tile action-tile--on"
        @click="onSchedule"
      >
        <span class="i-ri-calendar-schedule-line size-5 text-n-blue-11" />
        {{ $t('CONTACT_PANEL.ACTIONS.SCHEDULE') }}
      </button>
    </div>

    <!-- O motivo por escrito. Sem isto o atendente só vê um botão apagado e
         fica com a mesma dúvida de antes. -->
    <span
      v-if="canCall && !hasPhone"
      data-testid="contact-action-needs-phone"
      class="text-[13px] leading-snug text-n-slate-11"
    >
      {{ needsPhone }}
    </span>
  </div>
</template>

<style scoped>
/* flex-1 divide a linha por igual, com três ações ou com uma — ação única
   ocupa a linha inteira de propósito (CTA), nunca fica solta à esquerda.
   Sem NENHUMA ação possível, a barra inteira some (v-if no wrapper). */
.action-tile {
  @apply flex min-h-14 flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-2xl bg-n-alpha-2 px-1 py-2 text-center text-[12px] font-bold leading-tight text-n-slate-12 no-underline transition-colors;
}
.action-tile--on {
  @apply cursor-pointer hover:bg-n-alpha-3;
}
.action-tile--off {
  @apply cursor-not-allowed text-n-slate-10;
}
.action-tile:focus-visible {
  @apply outline outline-2 outline-offset-2 outline-n-brand;
}
</style>
